import type { Checklist } from '@cobliteam/api-lib';
import { CookieManager } from '@cobliteam/cookie-manager';
import { useTranslation } from '@cobliteam/i18n';
import { Button, Dialog } from '@cobliteam/ui/components';
import { Checkbox, Label, useFormContext } from '@cobliteam/ui/form';
import Icon from '@cobliteam/ui/icons';
import { useState } from 'react';

import type { FormValues } from '../checklistSchema';

interface ChecklistSaveButtonProps {
  checklist?: Checklist;
  onSubmit: (values: FormValues) => void;
}

export const REMINDER_COOKIE_NAME = 'not_show_publish_modal_reminder';

const ChecklistSaveButton = ({ checklist, onSubmit }: ChecklistSaveButtonProps) => {
  const { t } = useTranslation('checklists');
  const { formState, trigger, handleSubmit } = useFormContext<FormValues>();
  const { isDirty, errors, isSubmitting } = formState;

  const [checked, setChecked] = useState<boolean | 'indeterminate'>(false);

  if (checklist) {
    if (CookieManager.getCookie(REMINDER_COOKIE_NAME) || Object.keys(errors).length) {
      return (
        <Button variant='contained' startIcon={<Icon name='send-horizontal' />} disabled={!isDirty || isSubmitting} loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
          {t('save_changes_and_send')}
        </Button>
      );
    }

    return (
      <Dialog>
        <Dialog.Trigger asChild>
          <Button variant='contained' startIcon={<Icon name='send-horizontal' />} disabled={!isDirty || isSubmitting} loading={isSubmitting} onClick={() => trigger()}>
            {t('save_changes_and_send')}
          </Button>
        </Dialog.Trigger>

        <Dialog.Content size='sm'>
          <Dialog.Header>
            <Dialog.Title>{t('publish_changes')}</Dialog.Title>
            <Dialog.Close />
          </Dialog.Header>

          <Dialog.Body>
            <Dialog.Description>
              {t('changes_applied_from_publication')}
              <b>{t('from_this_publication')}</b>
              {t('previous_checklists_unaffected')}
            </Dialog.Description>

            <div className='mt-2.5 flex items-center gap-2.5'>
              <Checkbox id='reminder-checklist-publish' onCheckedChange={setChecked} />
              <Label htmlFor='reminder-checklist-publish' className='cursor-pointer'>
                {t('dont_show_again')}
              </Label>
            </div>
          </Dialog.Body>

          <Dialog.Footer className='flex items-center justify-end gap-2.5'>
            <Dialog.Close asChild>
              <Button variant='ghost'>{t('cancel')}</Button>
            </Dialog.Close>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={() => {
                if (checked) CookieManager.setCookie(REMINDER_COOKIE_NAME, 'true');
                handleSubmit(onSubmit)();
              }}
            >
              {t('publish')}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    );
  }

  return (
    <Button variant='contained' startIcon={<Icon name='send-horizontal' />} disabled={!isDirty || isSubmitting} loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
      {t('create_checklist_and_send')}
    </Button>
  );
};

export default ChecklistSaveButton;
